import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { Field, FormikProps } from "formik";
import { every, isEmpty, keys, noop, toLower, values, words } from "lodash";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import DropdownSearch from "../../../../components/forms/DropdownSearch";
import FormProvider from "../../../../components/forms/FormProvider";
import { getLabelColorStyle } from "../../../../components/labels/utils";
import { useAppearance } from "../../../../context/appearance";
import { useCommissionTemplates } from "../../../../context/commissionTemplates";
import { useLoading } from "../../../../context/loading";
import { LoadingKey } from "../../../../context/loading/LoadingProvider";
import { useWorkspaces } from "../../../../context/workspaces";
import classNames from "../../../../utils/classNames";
import { Entity } from "../../../../utils/constants";
import timezones from "../../../../utils/timezones";
import { CommissionTemplate, CommissionType } from "../../../../utils/types";
import useDebounce from "../../../../utils/useDebounce";
import EntityDataTable from "../../common/EntityDataTable";
import TablePropertiesSidebar from "../../common/TablePropertiesSidebar";
import TableSearchInput from "../../common/TableSearchInput";
import useDataTable, { Property } from "../../common/useDataTable";
import { PaginationKey } from "../../common/usePagination";
import CommissionsActionsButton from "./CommissionsActionsButton";
import CommissionsFormSidebar from "./CommissionsFormSidebar";

interface Props extends RouteComponentProps<{}> {}

const CommissionsPage: React.FC<Props> = () => {
  const { getIsReady } = useLoading();
  const { commissionTemplates, getCommissionValueLabel } =
    useCommissionTemplates();
  const searchInput = useDataTable(
    (state) => state.searchInput[Entity.commissionTemplate]
  );
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const [openCommissionFormSidebar, setOpenCommissionTemplateFormSidebar] =
    useState(false);
  const [currentlyEditedCommissionTemplate, setCurrentlyEditedCommission] =
    useState<CommissionTemplate | undefined>();
  const { isDark } = useAppearance();
  const { workspaces, currentWorkspaceId, updateWorkspace } = useWorkspaces();

  const toggleEditCommissionForm = useCallback(
    (commissionTemplate: CommissionTemplate | undefined) => {
      if (openCommissionFormSidebar) {
        setCurrentlyEditedCommission(undefined);
        setOpenCommissionTemplateFormSidebar(false);
      } else {
        setCurrentlyEditedCommission(commissionTemplate);
        setOpenCommissionTemplateFormSidebar(true);
      }
    },
    [openCommissionFormSidebar]
  );

  const filteredCommissions = useMemo(() => {
    let filteredList = values(commissionTemplates).filter(
      (commissionTemplate) => {
        if (debouncedSearchInput) {
          const searchInputWords = words(debouncedSearchInput).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(commissionTemplate.name).includes(word) ||
              commissionTemplate.type.includes(word)
          );
        }
        return true;
      }
    );
    return filteredList;
  }, [commissionTemplates, debouncedSearchInput]);

  const properties: { [key: string]: Property<CommissionTemplate> } = useMemo(
    () => ({
      template: {
        key: "template",
        title: "Template",
        renderContent: ({ name, primary }) => (
          <div className="whitespace-nowrap relative flex items-center space-x-3 group">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {name}
            </p>
            {primary && (
              <div
                className="inline-flex items-center rounded-full font-medium px-3 py-0.5 my-2 text-sm"
                style={getLabelColorStyle("#7057ff", isDark)}
              >
                default
              </div>
            )}
          </div>
        )
      },
      agreement: {
        key: "agreement",
        style: { minWidth: "14rem" },
        title: "Agreement",
        renderContent: getCommissionValueLabel
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (commissionTemplate) => (
          <CommissionsActionsButton
            commissionTemplate={commissionTemplate}
            toggleEditCommissionForm={toggleEditCommissionForm}
          />
        )
      }
    }),
    [getCommissionValueLabel, isDark, toggleEditCommissionForm]
  );

  const initialValues = useMemo(() => {
    const currentWorkspace = currentWorkspaceId
      ? workspaces[currentWorkspaceId]
      : undefined;

    return {
      timezone: currentWorkspace?.timezone
    };
  }, [currentWorkspaceId, workspaces]);

  const getTimezones = useCallback((searchInput: string | undefined) => {
    const filteredTimezones = keys(timezones).filter((timezone) => {
      if (searchInput) {
        const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(timezone).includes(word) ||
            toLower(timezones[timezone] || "").includes(word)
        );
      }
      return true;
    });

    return filteredTimezones;
  }, []);

  const isLoadingCommissionTemplates = useMemo(
    () =>
      isEmpty(commissionTemplates) &&
      !getIsReady([LoadingKey.workspaceCommissionTemplates]),
    [commissionTemplates, getIsReady]
  );

  const renderCommissionTemplates = useMemo(() => {
    if (isLoadingCommissionTemplates) {
      return <CenteredLoading className="h-96" />;
    }

    return (
      <>
        <div className="flex items-center justify-between pb-4 mt-6">
          <div className="w-7/12">
            <TableSearchInput
              entity={Entity.commissionTemplate}
              placeholder="Search a commission template by name..."
            />
          </div>

          <div className="flex items-center w-5/12 justify-end">
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <div>
                    <Menu.Button className="p-2 rounded-full bg-white flex items-center text-gray-400 hover:text-gray-600 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700">
                      <span className="sr-only">Open options</span>
                      <DotsVerticalIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>

                  <Transition
                    show={open}
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items
                      static
                      className="z-50 origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
                    >
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => {
                                setShowPropertiesSidebar(true);
                              }}
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                                  : "text-gray-700 dark:bg-gray-800 dark:text-white",
                                "w-full flex justify-between px-4 py-2 text-sm"
                              )}
                            >
                              <span>Properties</span>
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>

            <button
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={() => setOpenCommissionTemplateFormSidebar(true)}
            >
              Add commission template
            </button>
          </div>
        </div>

        <div>
          <EntityDataTable<CommissionTemplate>
            paginationKey={PaginationKey.settingsCommissionTemplates}
            entity={Entity.commissionTemplate}
            items={filteredCommissions}
            properties={properties}
          />
        </div>
      </>
    );
  }, [filteredCommissions, isLoadingCommissionTemplates, properties]);

  return (
    <>
      <CommissionsFormSidebar
        commission={currentlyEditedCommissionTemplate}
        open={openCommissionFormSidebar}
        toggleEditCommissionForm={toggleEditCommissionForm}
      />

      <TablePropertiesSidebar<CommissionTemplate>
        entity={Entity.commissionTemplate}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      <div className="flex flex-col w-full">
        <div>
          <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Commissions
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            With commissions you can keep track of{" "}
            <Link
              className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
              to="/workspace/contacts"
            >
              contacts
            </Link>{" "}
            that need to be paid for their involvement with your hotspot
            deployments. This can be done by adding{" "}
            <Link
              className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
              to="/workspace/installs"
            >
              installs
            </Link>{" "}
            for your hotspots and linking contacts to those installs by adding
            commissions.
          </p>
        </div>

        <div className="w-full my-6 align-middle inline-block">
          <div className="border-b border-gray-200 dark:border-gray-500 pb-5 mb-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Commission reports
            </h3>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-300">
            Commission reports are generated at the beginning of each month
            based on the previous month's{" "}
            <Link
              className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
              to="/workspace/installs"
            >
              installs
            </Link>{" "}
            and commissions data.
          </p>
        </div>

        <div className="flex">
          <div>
            <FormProvider
              classnames={{
                wrapper: "w-80",
                form: "dark:bg-transparent"
              }}
              formikProps={{
                onSubmit: noop,
                initialValues
              }}
              orderedFieldNames={ORDERED_FIELD_NAMES}
              withoutSubmitButton
            >
              {({ isSubmitting }: FormikProps<FormValues>) => {
                return (
                  <Field
                    className="-mt-2 "
                    label="Timezone for commission reports"
                    name="timezone"
                    placeholder="Search a timezone"
                    disabled={isSubmitting || !initialValues.timezone}
                    fetchItems={getTimezones}
                    itemToString={(item: string) => timezones[item]}
                    component={DropdownSearch}
                    withoutMinimumSearchCharacters
                    debounceTime={0}
                    initialItems={getTimezones("")}
                    clearInputOnSelect={false}
                    onSelectedItem={(timezone: string) => {
                      if (timezone) updateWorkspace({ timezone });
                    }}
                  />
                );
              }}
            </FormProvider>
          </div>
        </div>

        <div className="w-full my-6 align-middle inline-block">
          <div className="border-b border-gray-200 dark:border-gray-500 pb-5 mb-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Commission templates
            </h3>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-300">
            Streamline payments data entry with commission templates. The
            "default" commission template will be automatically applied to
            linked location contacts for new installs.
          </p>
        </div>

        {renderCommissionTemplates}
      </div>
    </>
  );
};

export default CommissionsPage;

//
// Utils
//

interface TypeItem {
  key: CommissionType;
  title: string;
}

export const commissionTypeOptions: TypeItem[] = [
  { key: CommissionType.percentage, title: "Percentage of rewards" },
  { key: CommissionType.recurringFixed, title: "Recurring fixed amount" },
  { key: CommissionType.oneTimeFixed, title: "One time fixed amount" }
];

interface FormValues {
  timezone: TypeItem;
}

const ORDERED_FIELD_NAMES = ["timezone"];
